import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateDirective } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { ITranslationSubscription } from '@app/core/interfaces/localization';
import { IDialogEventData } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-data.interface';
import { dialogEventProvider } from '@app/main/leak-detection/dialogs/dialog-event/providers/dialog-event.providers';
import { DialogEventTabs } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-tabs.enum';
import { dialogEventTabsLocalize } from '@app/main/leak-detection/dialogs/dialog-event/localize/dialog-event-tabs';
import { DialogEventHeaderComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-header/dialog-event-header.component';
import { DialogEventDetailsComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-details/dialog-event-details.component';
import { DialogEventHistoryComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-history/dialog-event-history.component';
import { DialogEventStatusBarComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-status-bar/dialog-event-status-bar.component';
import { DialogEventChangeFormComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-change-form/dialog-event-change-form.component';
import { DialogEventLocationEditComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-location-edit/dialog-event-location-edit.component';
import { DialogEventCreateComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-create/dialog-event-create.component';
import { DialogEventFooterComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-footer/dialog-event-footer.component';
import { DialogEventConfirmOverlayComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-confirm-overlay/dialog-event-confirm-overlay.component';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventHistory } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-history.service';
import { DialogEventTabsService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-tabs.service';
import { DialogEventOverlay } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-overlay.enum';
import { DialogEventEditor } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-editor.enum';

@Component({
  selector: 'gc-dialog-event',
  standalone: true,
  imports: [
    TranslateDirective,
    Button,
    DialogEventHeaderComponent,
    DialogEventDetailsComponent,
    DialogEventHistoryComponent,
    DialogEventStatusBarComponent,
    DialogEventChangeFormComponent,
    DialogEventLocationEditComponent,
    DialogEventCreateComponent,
    DialogEventFooterComponent,
    DialogEventConfirmOverlayComponent,
  ],
  templateUrl: './dialog-event.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [dialogEventProvider()],
})
export class DialogEventComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<IDialogEventData>>(DynamicDialogConfig);

  protected readonly dialogState = inject(DialogEventState);
  protected readonly tabs = inject(DialogEventTabsService);

  private readonly history = inject(DialogEventHistory);

  protected readonly title: ITranslationSubscription = this.config.data!.title;

  protected readonly EventTabs = DialogEventTabs;
  protected readonly Editor = DialogEventEditor;
  protected readonly tabLabels = dialogEventTabsLocalize;

  protected readonly tabOrder = [DialogEventTabs.Details, DialogEventTabs.History];

  protected readonly historyCount = this.history.historyCount;

  protected requestClose(): void {
    // nothing exists yet to lose changes of — the create popup closes without asking
    if (!this.dialogState.isCreating() && this.dialogState.isDirty()) {
      this.tabs.openOverlay(DialogEventOverlay.Discard);

      return;
    }

    this.close();
  }

  protected confirmOverlay(overlay: DialogEventOverlay): void {
    this.tabs.closeOverlay();

    switch (overlay) {
      case DialogEventOverlay.Discard:
        this.dialogState.discard();
        this.close();
        break;
      case DialogEventOverlay.Hide:
        this.dialogState.setHidden(true);
        break;
    }
  }

  protected close(): void {
    this.ref.close();
  }

  /** hands the new event to the opener, which shows it in its own popup */
  protected closeCreated(): void {
    this.ref.close(this.dialogState.event());
  }
}
